(ns manage.adset-decision-tree-page.view.tracker-table
  (:require ["@mui/material/Button$default" :as Button]
            ["@mui/material/Checkbox$default" :as Checkbox]
            ["@mui/material/FormControlLabel$default" :as FormControlLabel]
            ["@mui/material/Icon$default" :as Icon]
            ["@mui/material/InputAdornment$default" :as InputAdornment]
            ["@mui/material/Menu$default" :as Menu]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/TextField$default" :as TextField]
            ["@mui/material/Typography$default" :as Typography]
            ["@mui/x-data-grid-premium" :refer [DataGridPremium]]
            ["src/svg/wpp/filter/tune.svg$default" :as Tune]
            [cljs-bean.core :refer [->clj]]
            [clojure.set :as set]
            [clojure.string :as str]
            [common.util :refer [spread-obj]]
            [common.widgets.data-grid :as dg]
            [linked.core :as l]
            [manage.adset-decision-tree-page.node-library :as node-library]
            [manage.adset-decision-tree-page.sub :as dt-sub]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defui ad-variant-cell [{{:keys [adVariant decisionPath]} :row}]
  ($ Stack {:spacing 0.5 :sx #js {:minWidth 0 :my 1}}
     ($ Typography {:noWrap true} adVariant)
     (when (seq decisionPath)
       ($ Typography {:noWrap true :color "text.secondary"} decisionPath))))

(def ^:private ad-variant-column
  {:field "adVariant"
   :headerName "Ad variant"
   :flex 3
   :minWidth 200
   :disableColumnMenu true
   :renderCell (fn [^js params] ($ ad-variant-cell (->clj params)))})

(defn use-filters []
  (let [[path-filter set-path-filter] (uix/use-state {})
        toggle-filter (uix/use-callback
                       (fn [group-name value]
                         (set-path-filter
                          (fn [selected]
                            (let [current-group (get selected group-name #{})]
                              (if (contains? current-group value)
                                (let [updated-group (disj current-group value)]
                                  (if (empty? updated-group)
                                    (dissoc selected group-name)
                                    (assoc selected group-name updated-group)))
                                (assoc selected group-name (conj current-group value)))))))
                       [])
        filter-rows (uix/use-callback
                     (fn [rows]
                       (if (empty? path-filter)
                         rows
                         (filter
                          (fn [row]
                            (when-let [path (:decisionPath row)]
                              (let [segments (set (str/split path #" / "))]
                                (every? (fn [[_group-name selected-outcomes]]
                                          (some selected-outcomes segments))
                                        path-filter))))
                          rows)))
                     [path-filter])]
    {:path-filter path-filter
     :set-path-filter set-path-filter
     :toggle-filter toggle-filter
     :filter-rows filter-rows}))

(defn- build-filter-options-from-tree [node-id->node-data root-node-id]
  (let [node-id->children (->> (vals node-id->node-data)
                               (group-by :parent-id))
        process-decision
        (fn [decision-node]
          (let [outcomes (sort-by :ordinal (get node-id->children (:id decision-node)))
                next-decisions (mapcat (comp node-id->children :id) outcomes)]
            {:group (when (seq outcomes)
                      [(get-in node-library/type->node [(:type decision-node) :name])
                       (set (map :label outcomes))])
             :next-decisions next-decisions}))
        bfs (fn bfs [decision-nodes]
              (when (seq decision-nodes)
                (let [results (map process-decision decision-nodes)
                      groups (keep :group results)
                      next-level (mapcat :next-decisions results)]
                  (concat groups (bfs next-level)))))]
    (reduce (fn [acc [k v]] (update acc k set/union v))
            (l/map)
            (bfs (get node-id->children root-node-id)))))

(defn use-filter-menu []
  (let [node-id->node-data (use-subscribe [::dt-sub/node-id->node-data])
        root-node-id (use-subscribe [::dt-sub/root-node-id])
        filter-options (uix/use-memo
                        #(build-filter-options-from-tree node-id->node-data root-node-id)
                        [node-id->node-data root-node-id])
        [search-text set-search-text] (uix/use-state "")
        filtered-options
        (uix/use-memo
         (fn []
           (if (empty? search-text)
             filter-options
             (let [search-lower (str/lower-case search-text)]
               (keep (fn [[decision-name labels]]
                       (when-let [filtered-labels
                                  (seq (filter #(str/includes? (str/lower-case %)
                                                               search-lower)
                                               labels))]
                         [decision-name filtered-labels]))
                     filter-options))))
         [search-text filter-options])]
    {:search-text search-text
     :set-search-text set-search-text
     :filtered-options filtered-options}))

(defui path-filter-groups
  [{:keys [path-filter set-path-filter toggle-filter filtered-options]}]
  ($ Stack {:spacing 2}
     (for [[decision-node-name outcomes] filtered-options
           :let [outcomes-set (set outcomes)
                 all-selected (= outcomes-set (get path-filter decision-node-name))]]
       ($ Stack {:key decision-node-name :spacing 0.5}
          ($ Stack {:direction "row"
                    :justifyContent "space-between"
                    :alignItems "center"}
             ($ Typography {:sx #js {:fontWeight 600}}
                decision-node-name)
             ($ Button {:variant "text"
                        :onClick
                        (fn [^js _event]
                          (set-path-filter
                           #(if all-selected
                              (dissoc % decision-node-name)
                              (assoc % decision-node-name outcomes-set))))}
                (if all-selected "Deselect all" "Select all")))
          (for [outcome outcomes]
            ($ FormControlLabel
               {:key outcome
                :control ($ Checkbox
                            {:size "small"
                             :checked (contains? (get path-filter decision-node-name #{})
                                                 outcome)
                             :onChange #(toggle-filter decision-node-name outcome)
                             :sx #js {:pl 0}})
                :label outcome}))))))

(defui filter-menu [{:keys [path-filter toggle-filter set-path-filter]}]
  (let [{:keys [search-text set-search-text filtered-options]} (use-filter-menu)
        [anchor-el set-anchor-el] (uix/use-state nil)
        filter-count (reduce + (map count (vals path-filter)))
        button-text (if (pos? filter-count)
                      (str "Filters (" filter-count ")")
                      "Filters")]
    ($ Stack
       ($ Button {:variant "outlined"
                  :startIcon ($ SvgIcon {:component Tune
                                         :viewBox "0 0 17 17"
                                         :sx #js {:ml 1}})
                  :sx #js {:color "grey.20"
                           :borderColor "grey.20"}
                  :onClick (fn [^js e] (set-anchor-el (.-currentTarget e)))}
          button-text)
       ($ Menu {:open (boolean anchor-el)
                :anchorEl anchor-el
                :onClose #(set-anchor-el nil)
                :PaperProps #js {:sx #js {:width 320
                                          :maxHeight 500}}}
          ($ Stack {:spacing 2 :sx #js {:p 2}}
             ($ TextField {:placeholder "Search"
                           :size "small"
                           :fullWidth true
                           :value search-text
                           :onChange #(set-search-text (.. % -target -value))
                           :InputProps #js {:startAdornment
                                            ($ InputAdornment {:position "start"}
                                               ($ Icon "search"))}})
             (when (seq filtered-options)
               ($ path-filter-groups {:path-filter path-filter
                                      :set-path-filter set-path-filter
                                      :toggle-filter toggle-filter
                                      :filtered-options filtered-options})))))))

(def TrackerToolbar
  (uix/as-react
   (fn [{:keys [path-filter set-path-filter toggle-filter toolbar-extra]}]
     ($ dg/grid-toolbar
        ($ Stack {:direction "row" :justifyContent "space-between"
                  :alignItems "center" :sx #js {:width "100%"}}
           ($ filter-menu {:path-filter path-filter
                           :set-path-filter set-path-filter
                           :toggle-filter toggle-filter})
           toolbar-extra)))))

(defui tracker-table [{:keys [rows extra-columns process-row-update
                              toolbar-extra row-height sx dg-props]}]
  (let [{:keys [filter-rows path-filter toggle-filter set-path-filter]} (use-filters)
        filtered-rows (uix/use-memo
                       #(clj->js (filter-rows (->clj rows)))
                       [rows filter-rows])
        columns (uix/use-memo
                 #(clj->js (into [ad-variant-column] extra-columns))
                 [extra-columns])
        toolbar-props (uix/use-memo
                       (fn [] #js {:path-filter path-filter
                                   :toggle-filter toggle-filter
                                   :set-path-filter set-path-filter
                                   :toolbar-extra toolbar-extra})
                       [path-filter toggle-filter set-path-filter toolbar-extra])]
    ($ DataGridPremium
       (merge {:columns columns
               :rows filtered-rows
               :getRowId #(.-id %)
               :rowHeight (or row-height 60)
               :disableRowSelectionOnClick true
               :processRowUpdate process-row-update
               :hideFooter true
               :slots #js {:toolbar TrackerToolbar}
               :slotProps (spread-obj #js {:baseTextField dg/grid-toolbar-outlined-input}
                                      #js {:toolbar toolbar-props})
               :sx sx}
              dg-props))))
